import { useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FaBold, FaItalic, FaCode, FaLink, FaImage, FaListUl, FaListOl, FaQuoteRight, FaHeading, FaEye, FaColumns, FaPen, FaStrikethrough } from 'react-icons/fa';

const BLOCK = 'LStudio__app_rte';

/*
    🔥 EDITOR — Markdown

    El más honesto de los tres: lo que escribís es lo que se guarda.
    Sin HTML sucio, sin estilos pegados al copiar desde Word, y
    versionable en git si hiciera falta.

    ⚠️ EL SANITIZADO NO ES OPCIONAL
       marked convierte markdown en HTML, y markdown ACEPTA HTML crudo.
       Sin DOMPurify, un usuario podría escribir <script> y ejecutarlo
       en la sesión de otro. Es el clásico XSS almacenado.

    npm i marked dompurify
*/

const MODES = [
    { id: 'write', label: 'Escribir', icon: <FaPen /> },
    { id: 'split', label: 'Dividido', icon: <FaColumns /> },
    { id: 'preview', label: 'Vista', icon: <FaEye /> },
];

/*
    Envuelve la selección con marcas, o inserta la plantilla si no hay
    nada seleccionado. Es lo que hace que la barra sirva de verdad.
*/
const onWrap = (area, value, onChange, before, after = before, placeholder = 'texto') => {

    const START = area.selectionStart;
    const END = area.selectionEnd;

    const SELECTED = value.slice(START, END) || placeholder;

    const NEXT = `${value.slice(0, START)}${before}${SELECTED}${after}${value.slice(END)}`;

    onChange?.(NEXT);

    requestAnimationFrame(() => {
        area.focus();
        area.selectionStart = START + before.length;
        area.selectionEnd = START + before.length + SELECTED.length;
    });
};

const onPrefixLines = (area, value, onChange, prefix) => {

    const START = area.selectionStart;
    const END = area.selectionEnd;

    const BEFORE = value.lastIndexOf('\n', START - 1) + 1;

    const BLOCK_TEXT = value.slice(BEFORE, END) || 'texto';

    const NEXT_BLOCK = BLOCK_TEXT.split('\n').map((line) => `${prefix}${line}`).join('\n');

    onChange?.(`${value.slice(0, BEFORE)}${NEXT_BLOCK}${value.slice(END)}`);

    requestAnimationFrame(() => area.focus());
};

export const RichTextMarkdown = ({
    value = '', onChange, placeholder = 'Escribí en Markdown…',
    isDisabled = false, minHeight = 200, defaultMode = 'split',
}) => {

    const REF__useRefArea = useRef(null);

    const [useMode, setMode] = useState(defaultMode);

    const MEMO__HTML = useMemo(() => {

        if (!value) return '';

        // ⚠️ marked → DOMPurify. Nunca uno sin el otro.
        return DOMPurify.sanitize(marked.parse(value, { breaks: true, gfm: true }));

    }, [value]);

    const onEmit = (next) => onChange?.({ target: { value: next } });

    const onAction = (fn) => () => {
        const AREA = REF__useRefArea.current;
        if (!AREA || isDisabled) return;
        fn(AREA, value, onEmit);
    };

    const onKeyDown = (event) => {

        // Tab inserta dos espacios en vez de saltar de campo
        if (event.key === 'Tab') {
            event.preventDefault();
            const AREA = event.target;
            const POS = AREA.selectionStart;
            onEmit(`${value.slice(0, POS)}  ${value.slice(AREA.selectionEnd)}`);
            requestAnimationFrame(() => { AREA.selectionStart = AREA.selectionEnd = POS + 2; });
        }
    };

    return (
        <div className={`${BLOCK} ${BLOCK}--markdown`} data-mode={useMode} data-disabled={isDisabled}
            style={{ '--LS__rte_min_height': `${minHeight}px` }} >

            <div className={`${BLOCK}__toolbar`}>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Título"
                        onClick={onAction((a, v, c) => onPrefixLines(a, v, c, '## '))}><FaHeading /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Negrita"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '**'))}><FaBold /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Cursiva"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '_'))}><FaItalic /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Tachado"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '~~'))}><FaStrikethrough /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Código"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '`'))}><FaCode /></button>
                </div>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Lista"
                        onClick={onAction((a, v, c) => onPrefixLines(a, v, c, '- '))}><FaListUl /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Lista numerada"
                        onClick={onAction((a, v, c) => onPrefixLines(a, v, c, '1. '))}><FaListOl /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Cita"
                        onClick={onAction((a, v, c) => onPrefixLines(a, v, c, '> '))}><FaQuoteRight /></button>
                </div>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Enlace"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '[', '](https://)', 'texto'))}><FaLink /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Imagen"
                        onClick={onAction((a, v, c) => onWrap(a, v, c, '![', '](https://)', 'alt'))}><FaImage /></button>
                </div>

                <div className={`${BLOCK}__group ${BLOCK}__group--end`}>
                    {
                        MODES.map((mode) => (
                            <button key={mode.id} type="button" className={`${BLOCK}__tool`}
                                data-on={useMode === mode.id} title={mode.label}
                                onClick={() => setMode(mode.id)} >
                                {mode.icon}
                            </button>
                        ))
                    }
                </div>

            </div>

            <div className={`${BLOCK}__panes`}>

                {
                    useMode !== 'preview' && (
                        <textarea ref={REF__useRefArea} className={`${BLOCK}__source`}
                            value={value} onChange={onChange} onKeyDown={onKeyDown}
                            placeholder={placeholder} disabled={isDisabled}
                            spellCheck="false" autoComplete="off" />
                    )
                }

                {
                    useMode !== 'write' && (
                        <div className={`${BLOCK}__preview ${BLOCK}__content`}
                            dangerouslySetInnerHTML={{ __html: MEMO__HTML }} />
                    )
                }

            </div>

        </div>
    );
};
